/** API `CashSession.Status` — numeric enum from backend. */
export enum CashSessionStatus {
  Open = 0,
  Closing = 1,
  PendingReview = 2,
  Closed = 3,
}

/** C#/Newtonsoft `StringEnumConverter` payloads (`Status: "Open"`). */
const STATUS_FROM_STRING_NAME: Record<string, CashSessionStatus> = {
  Open: CashSessionStatus.Open,
  Closing: CashSessionStatus.Closing,
  PendingReview: CashSessionStatus.PendingReview,
  Closed: CashSessionStatus.Closed,
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Kept for the narrow set of call sites that still receive unknown input
 * (external events). Post-PR 1, domain call sites use enum comparison directly.
 */
export const normalizeCashSessionStatus = (
  value: number | string | null | undefined,
): CashSessionStatus | null => {
  if (typeof value === 'string') {
    const byName = STATUS_FROM_STRING_NAME[value];
    if (byName !== undefined) return byName;
  }
  const n = toFiniteNumber(value);
  if (n !== null && n >= 0 && n <= 3 && Number.isInteger(n)) {
    return n as CashSessionStatus;
  }
  return null;
};

/** Resolves CashSession payloads that use PascalCase or camelCase id fields (`CashSessionId` / `cashSessionId`). */
export const readCashSessionId = (
  session: { CashSessionId?: string | null } | null | undefined,
): string | null => {
  if (session == null) return null;
  const r = session as { CashSessionId?: string | null; cashSessionId?: string | null };
  const id = r.CashSessionId ?? r.cashSessionId;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

/**
 * True when the session is in a closing/review transition — between Open and Closed.
 * Card-level UI uses this to render a single "Em análise" affordance covering both states.
 */
export const isCashSessionInAnalysis = (
  session: { Status: CashSessionStatus } | null | undefined,
): boolean => {
  if (!session) return false;
  return (
    session.Status === CashSessionStatus.Closing ||
    session.Status === CashSessionStatus.PendingReview
  );
};
